import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import Axios from "../apis/axios";
import { NodeId } from "components/NodeId";
import { produce } from "immer";

class Deferred {
  promise: Promise<any>;
  resolve!: (value?: any) => void;
  reject!: (reason?: any) => void;

  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

const createAttachmentSlice = (set: (data: any) => void, get: () => any) => ({

  allFilesUploaded: null,

  resetFilesFlag: () => {
    set({
      allFilesUploaded: null
    })
  },

  createAttachment: (payload: any, attachmentOfNodeId: string) => {
    if (payload.lang === undefined) {
      payload.lang = get().lang;
    }

    if (payload.nodeid === undefined) {
      payload.nodeid = new NodeId("mesutronic.de", uuidv4()).toUrl();
    }

    payload.isattachmentof = attachmentOfNodeId;

    const attachmentCommandObjects = [];
    if (payload.attachments?.length > 0) {
      for (const item of payload.attachments) {
        const attachment = item.attachment;
        if (attachment.nodeid === undefined || attachment.nodeid === null) {
          attachment.nodeid = new NodeId("mesutronic.de", uuidv4()).toUrl();
        }

        const attachmentPayload = {
          isattachmentof: payload.isattachmentof,
          lang: payload.lang,
          nodeid: attachment.nodeid,
          file: attachment.file,
          title: payload.title,
          description: payload.description,
          specifier: payload.specifier,
        };

        const fileToUpload = _.cloneDeep(attachment);
        fileToUpload.property = "file";
        const attachmentCommandObject = {
          parentNodeIds: [attachmentOfNodeId],
          nodeId: payload.nodeid,
          payload: attachmentPayload,
          filesToUpload: [fileToUpload],
          type: "create",
          task: "processCreateAttachmentCommand",
        };
        attachmentCommandObjects.push(attachmentCommandObject);
      }
    }

    const promises = get().enqueueCommands(attachmentCommandObjects);

    return promises;
  },

  updateAttachment: async (payload: any) => {
    let filesToUpload;

    const attachment = payload.attachment;
    attachment.nodeid = payload.nodeid;
    let fileToUpload = _.cloneDeep(attachment);
    fileToUpload.property = "file";
    filesToUpload = [fileToUpload];
    const commandObjects: any[] = [];
    delete payload.image;
    const attachmentCommandObject = {
      parentNodeIds: [payload.isattachmentof],
      filesToUpload: filesToUpload,
      nodeId: payload.nodeid,
      payload: payload,
      type: "update",
      task: "processUpdateGenericAttachmentCommand",
    };
    commandObjects.push(attachmentCommandObject);

    const promises = await get().enqueueCommands(commandObjects);

    return promises;
  },

  deleteAttachment: async (
    //added additional parameter "equipmentNodeId" because of faq , workFlow etc , we need it for which equipment this faq belong to so that we can take that equipment and edit its faq
    attachmentNodeId: string,
    attachmentOfNodeId: string
  ) => {
    let promises;

    const payload: any = {};
    payload.nodeid = attachmentNodeId;
    const deleteCommandObject = {
      parentNodeIds: [attachmentOfNodeId],
      nodeId: attachmentNodeId,
      payload: payload,
      type: "delete",
      task: "processDeleteAttachmentCommand",
    };
    promises = get().enqueueCommands([deleteCommandObject]);

    const promise = new Deferred();
    promise.resolve();
    promises = [promise];

    return promises;
  },

  processCreateAttachmentCommand: async (commandObject: any) => {
    try {
      if (commandObject.filesToUpload.length > 0) {
        commandObject.payload.file = commandObject.filesToUpload[0].uploadInfo;
      }

      if (get().isLoggedIn === true) {
        const response = await Axios.post(
          "api/v1/attachments",
          commandObject.payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        if (response && response?.status == 200) {
          return response.data;
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      
      throw new Error(error as string);
    }
  },

  processUpdateAttachmentCommand: async (commandObject: any) => {
    try {

      if (commandObject.filesToUpload.length > 0) {
        commandObject.payload.file = commandObject.filesToUpload[0].uploadInfo;
      }

      if (get().isLoggedIn === true) {
        const encodedAttachmentNodeId = encodeURIComponent(
          commandObject.payload.nodeid
        );
        const response = await Axios.put(
          "/api/v1/attachments/" + encodedAttachmentNodeId,
          commandObject.payload,
          {
            headers: {
              "Content-Type": "application/json",
              
            },
            timeout: 10000,
          }
        );

        if (response && response?.status == 200) {
          return;
        } else {
          throw new Error();
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(error as string);
    }
  },

  processDeleteAttachmentCommand: async (commandObject: any) => {
    try {

      if (get().isLoggedIn === true) {
        const encodedAttachmentNodeId = encodeURIComponent(
          commandObject.payload.nodeid
        );

        const response = await Axios.delete(
          "/api/v1/attachments/" + encodedAttachmentNodeId,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        if (response && response?.status == 200) {
          return;
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      
      throw new Error(error as string);
    }
  },

  processUpdateGenericAttachmentCommand: async (commandObject: any) => {
    try {
  

      if (commandObject.filesToUpload.length > 0) {
        commandObject.payload.file = commandObject.filesToUpload[0].uploadInfo;
        delete commandObject.payload.attachment;
      }

      if (get().isLoggedIn === true) {
        const encodedAttachmentNodeId = encodeURIComponent(
          commandObject.payload.nodeid
        );

        const response = await Axios.put(
          "/api/v1/attachments/" + encodedAttachmentNodeId,
          commandObject.payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        if (response && response?.status == 200) {
        
          return;
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      
      throw new Error(error as string);
    }
  },

  createNewAttachmentObject: (commandObject: any) => {
    const attachment = _.cloneDeep(
      commandObject[0]?.payload
        ? commandObject[0]?.payload
        : commandObject.payload
    );
    return attachment;
  },

  createNewAttachmentObjectForUpdateAttachment: (commandObject: any) => {
    const attachment = _.cloneDeep(commandObject[0].payload);
    return attachment;
  },
});

export default createAttachmentSlice;
